import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import useModal from "hooks/modals/useModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  GetRegisters,
  GetRegisterAdjustments,
  PostRegisterAdjustment,
  PutRegisterAdjustment,
  DeleteRegisterAdjustment,
} from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { GetAccountingReference } from "api/rpc/2024-04/facility";
import { StatusCode } from "api/protocols";

import { showError, showSuccess } from "redux/actions/ui";

import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import FilterDropdown from "components/filterDropDown/FilterDropDown";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input";
import SelectNew from "components/select/SelectNew";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import FormLayout from "components/form/FormLayout";
import TextField from "components/form/textField/TextField";
import Popup from "components/popup/Popup";
import Card from "components/card/Card";
import DropFilter from "components/dropdown/DropFilter";

import { IFacilityStaff } from "redux/reducers/models/facility";
import { TAccountingReference } from "redux/reducers/models/transaction";
import { IRegister } from "../../Admin";
import moment from "moment";
import { faPen, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { LocaleCurrency } from "helpers/Locale";

interface IAdjustmentState {
  date: Date | null;
  selected_registers_ids: number[] | null;
}

interface IAdjustmentModalState {
  id: number | null;
  register_id: number | null;
  date: string;
  amount: string | null;
  reason: string | null;
  payment_reference: number | null;
  offsetting_reference: number | null;
  admin_id: number | null;
}

export interface IRegisterAdjustment {
  id: number;
  register_id: number;
  date: string;
  amount: number;
  payment_accounting_reference_id: number;
  offsetting_accounting_reference_id: number;
  reason: string;
  admin_id: number;
  payment_accounting_reference: TAccountingReference;
  offsetting_accounting_reference: TAccountingReference;
  user: IFacilityStaff;
  register: IRegister;
  register_filter_id: number;
  date_filter: string;
}

export default function Adjustments() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const windowSize = useWindowSize();

  const [accountingReferences, setAccountingReferences] = useState<TAccountingReference[]>(undefined);
  const [registers, setRegisters] = useState<IRegister[]>(undefined);
  const [adjustments, setAdjustments] = useState<IRegisterAdjustment[]>(undefined);
  const [adjustmentState, setAdjustmentState] = useState<IAdjustmentState>({
    date: null,
    selected_registers_ids: null,
  });

  const {
    state: newState,
    updateModal: updateNewState,
    closeModal: closeNewState,
  } = useModal<IAdjustmentModalState>({
    id: null,
    register_id: null,
    date: moment(new Date()).format("YYYY-MM-DD"),
    amount: null,
    reason: null,
    payment_reference: null,
    offsetting_reference: null,
    admin_id: null,
    clearOnClose: true,
  });

  const {
    state: deleteState,
    updateModal: updateDeleteState,
    closeModal: closeDeleteState,
  } = useModal({ adjustment_id: null });

  useEffect(() => {
    void loadAccountingReferences();
    void loadRegisters();
  }, []);

  useEffect(() => {
    void loadAdjustments();
  }, [adjustmentState]);

  async function loadAccountingReferences() {
    const refRes = await GetAccountingReference(null, true);

    if (refRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading accounting references")));
      return;
    }

    setAccountingReferences(refRes.data as TAccountingReference[]);
  }

  async function loadRegisters() {
    const registerRes = await GetRegisters(null, true);

    if (registerRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading registers")));
      return;
    }

    setRegisters(registerRes.data);
  }

  async function loadAdjustments() {
    const adjustmentRes = await GetRegisterAdjustments(
      {
        register_ids: adjustmentState.selected_registers_ids,
        date: adjustmentState.date ? moment(adjustmentState.date).format("YYYY-MM-DD") : null,
      },
      true,
    );

    if (adjustmentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading adjustments")));
      return;
    }

    setAdjustments(adjustmentRes.data as IRegisterAdjustment[]);
  }

  async function saveAdjustment() {
    const params = {
      register_id: newState.register_id,
      date: newState.date,
      amount: parseFloat(newState.amount ?? ""),
      payment_accounting_reference_id: newState.payment_reference,
      offsetting_accounting_reference_id: newState.offsetting_reference,
      reason: newState.reason,
    };

    const adjustmentRes = newState.id
      ? await PutRegisterAdjustment({ id: newState.id, ...params }, true)
      : await PostRegisterAdjustment(params, true);

    if (adjustmentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading adjustments")));
      return;
    }

    dispatch(showSuccess(t(newState.id ? "Adjustment updated successfully" : "Adjustment created successfully")));
    void loadAdjustments();
    closeNewState();
  }

  function openEditAdjustment(adjustment: IRegisterAdjustment) {
    updateNewState({
      isOpen: true,
      id: adjustment.id,
      register_id: adjustment.register?.id,
      date: adjustment.date,
      amount: String(adjustment.amount),
      reason: adjustment.reason,
      payment_reference:
        adjustment.payment_accounting_reference?.id != null ? Number(adjustment.payment_accounting_reference.id) : null,
      offsetting_reference:
        adjustment.offsetting_accounting_reference?.id != null
          ? Number(adjustment.offsetting_accounting_reference.id)
          : null,
    });
  }

  async function deleteAdjustment() {
    if (!deleteState.adjustment_id) {
      return;
    }

    const adjustmentRes = await DeleteRegisterAdjustment({ id: deleteState.adjustment_id }, true);

    if (adjustmentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting adjustment")));
      return;
    }

    dispatch(showSuccess(t("Adjustment deleted successfully")));
    void loadAdjustments();
    closeDeleteState();
  }

  function setNewAdjustmentDate(date: Date) {
    updateNewState({ date: moment(date).format("YYYY-MM-DD") });
  }

  function onCheckBoxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value, checked } = e.target;
    setAdjustmentState(prevState => {
      const current = prevState.selected_registers_ids ?? [];
      if (checked) {
        return { ...prevState, selected_registers_ids: [...current, Number(value)] };
      }
      return { ...prevState, selected_registers_ids: current.filter(registerId => registerId !== Number(value)) };
    });
  }

  function handleRemoveAll() {
    setAdjustmentState(prevState => ({ ...prevState, selected_registers_ids: [] }));
  }

  function handleAddAllRegisters() {
    const updatedSelectedRegisters = registers?.map(register => register.id) ?? [];
    setAdjustmentState(prevState => ({
      ...prevState,
      selected_registers_ids: updatedSelectedRegisters,
    }));
  }

  const primaryAction = {
    content: t("New Adjustment"),
    action: () =>
      updateNewState({
        isOpen: true,
        id: null,
        register_id: null,
        date: moment(new Date()).format("YYYY-MM-DD"),
        amount: null,
        reason: null,
        payment_reference: null,
        offsetting_reference: null,
      }),
  };

  return (
    <Page title={t("Register Adjustments")} primaryAction={primaryAction}>
      <div className="flex gap-2 items-center pb-2">
        <DropFilter
          key="date"
          title={t("secure.facility.order.order_filters.001")}
          filterData={[{ id: adjustmentState.date, label: t("secure.facility.order.order_filters.002") }]}
          filterLabelPropFromData="label"
          filterIdPropFromData="id"
          filterType="Date"
          applyFilters={(filterReturn: Record<string, any>[]) => {
            setAdjustmentState(
              filterReturn.length > 0
                ? { date: filterReturn[0].id, selected_registers_ids: adjustmentState.selected_registers_ids }
                : { date: null, selected_registers_ids: adjustmentState.selected_registers_ids },
            );
          }}
          leftAligned
          save
        />
        <FilterDropdown
          label={t("Registers")}
          data={registers}
          dataLabelProperty={"title"}
          checkBoxId={"selected_registers_ids"}
          dataValueProperty="id"
          onCheckBoxChange={onCheckBoxChange}
          onClear={handleRemoveAll}
          onAddAll={handleAddAllRegisters}
          leftAligned={windowSize.width <= MOBILE_WIDTH}
        />
      </div>
      <Card>
        <Card.Section table>
          <DataTable
            columns={[
              { label: t("Date") },
              { label: t("Register") },
              { label: t("Amount") },
              { label: t("Reason") },
              { label: t("Payment Reference #") },
              { label: t("Offsetting Reference #") },
              { label: t("Admin") },
              { label: t("Actions") },
            ]}
          >
            {adjustments?.map((adjustment, index) => (
              <tr key={index}>
                <td>{moment(adjustment.date, "YYYY-MM-DD").format("MMMM Do YYYY")}</td>
                <td>{adjustment.register?.title}</td>
                <td>{<LocaleCurrency amount={adjustment.amount} />}</td>
                <td>{adjustment.reason}</td>
                <td>{adjustment.payment_accounting_reference?.title}</td>
                <td>{adjustment.offsetting_accounting_reference?.title}</td>
                <td>{adjustment.user?.full_name}</td>
                <td>
                  <FontAwesomeIcon className="mr-2 cursor-pointer" icon={faPen} onClick={() => openEditAdjustment(adjustment)} />
                  <FontAwesomeIcon
                    className="cursor-pointer"
                    icon={faTrash}
                    onClick={() => updateDeleteState({ isOpen: true, adjustment_id: adjustment.id })}
                  />
                </td>
              </tr>
            ))}
          </DataTable>
        </Card.Section>
      </Card>

      <Sheet
        title={t(newState.id ? "Edit Register Adjustment" : "New Register Adjustment")}
        open={newState.isOpen}
        onCancel={closeNewState}
        size="small"
        onOk={saveAdjustment}
        overflow
      >
        <FormLayout>
          <FormLayout.Group>
            <DatePickerInput
              startingDate={moment(newState.date, "YYYY-MM-DD").toDate()}
              setStartingDate={setNewAdjustmentDate}
              months={1}
              position="left"
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <SelectNew
              label={t("Register")}
              id="register_select"
              options={registers?.map(register => ({ label: register.title, id: register.id }))}
              onChange={value => updateNewState({ register_id: value })}
              value={newState.register_id}
            />
            <Input
              prefix={"$"}
              label={t("Amount")}
              step="0.01"
              onChange={e => updateNewState({ amount: e.target.value })}
              value={newState.amount}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <SelectNew
              label={t("Payment Accounting Reference")}
              id="payment_ar_select"
              options={accountingReferences?.map(register => ({ label: register.title, id: register.id }))}
              onChange={value => updateNewState({ payment_reference: value })}
              value={newState.payment_reference}
            />
            <SelectNew
              label={t("Offsetting Accounting Reference")}
              id="offset_ar_select"
              options={accountingReferences?.map(register => ({ label: register.title, id: register.id }))}
              onChange={value => updateNewState({ offsetting_reference: value })}
              value={newState.offsetting_reference}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <TextField
              label={t("Reason")}
              value={newState.reason}
              onChange={e => updateNewState({ reason: e.target.value })}
            />
          </FormLayout.Group>
        </FormLayout>
      </Sheet>

      <Popup
        open={deleteState.isOpen}
        title={t("Delete Register Adjustment")}
        description={t("Are you sure you want to delete this register adjustment?")}
        type="warning"
        onCancel={closeDeleteState}
        onOk={deleteAdjustment}
        okText={t("Delete")}
      />
    </Page>
  );
}
